import * as Sentry from '@sentry/react';

/**
 * Noop reutilizable como dispose cuando no hay nada que liberar
 * (instancia inválida o ya instrumentada). Tener un noop nombrado
 * evita que ESLint marque `no-empty-function` en arrow vacías y
 * además facilita debugging si aparece en stacks.
 */
function noopDispose() {
  /* intentionally empty */
}

/**
 * WeakMap<pusherInstance, disposeFn> de instancias ya instrumentadas.
 * Garantiza idempotencia frente a HMR, doble wire-up accidental o tests
 * que reúsan la misma instancia sin disponer la anterior. Usamos
 * WeakMap para no retener referencias y permitir GC normal del Pusher
 * cuando deja de usarse.
 *
 * Importante: una segunda llamada a `instrumentPusher(pusher)` retorna
 * EL MISMO disposer real que la primera, no un noop. Esto evita que un
 * caller que sobreescribe su variable de cleanup con el segundo return
 * pierda silenciosamente la capacidad de unbind.
 */
const instrumented = new WeakMap();

/**
 * Ventana de dedupe (ms) para `captureMessage`. Durante reconnect storms
 * o caídas prolongadas, pusher-js puede emitir muchos `error`/`failed`/
 * `unavailable` seguidos. Sin throttling generaríamos cientos de issues
 * duplicados en Sentry, quemando cuota y enterrando el evento real.
 * Los breadcrumbs siguen emitiéndose en cada evento (son baratos).
 */
const CAPTURE_DEDUPE_WINDOW_MS = 30 * 1000;

/**
 * Engancha listeners al objeto Pusher para emitir breadcrumbs a Sentry
 * cuando hay cambios de conexión, reconexiones o errores. NO modifica
 * el comportamiento del cliente.
 *
 * Por qué importa: el dashboard depende de subscriptions GraphQL vía WS
 * y eventos Pusher. Cuando un agente reporta "no me llegan los mensajes",
 * los breadcrumbs de pusher en Sentry permiten saber si fue desconexión
 * de red, problema del servidor de Pusher, o algo del lado del cliente.
 *
 * Estados de pusher-js (referencia):
 *   connecting -> connected -> disconnected -> connecting (loop on retry)
 *   unavailable (retry loop indefinido sin red) -> connecting / disconnected
 *   failed (terminal, no auto-retry)
 *
 * Comportamiento:
 *  - `state_change`: breadcrumb siempre. `captureMessage` (dedupado) cuando
 *    el nuevo estado es `failed` o `unavailable` — ambos son síntomas de
 *    "no llegan mensajes" en producción. Mensaje y fingerprint usan
 *    constantes ('pusher-connection-failed' / 'pusher-connection-unavailable')
 *    para que Sentry agrupe los eventos en pocos issues, no por sesión.
 *  - `error`: breadcrumb siempre. `captureMessage` dedupado por
 *    `error:${type}:${code}` (NO incluye el mensaje, alineado con el
 *    fingerprint para que un solo issue agrupe todas las ocurrencias del
 *    mismo type+code aunque el texto del mensaje varíe con detalles
 *    dinámicos). Mensaje constante 'Pusher error' +
 *    `fingerprint: ['pusher-error', type, code]`.
 *  - Idempotente: instrumentar dos veces la misma instancia no duplica
 *    listeners. La segunda llamada retorna EL MISMO disposer real que
 *    la primera, así un caller que sobreescriba su variable de cleanup
 *    no pierde la capacidad de unbind.
 *
 * Limitaciones conocidas (documentadas, no son bugs):
 *  - **HMR**: el WeakMap `instrumented` vive en el módulo. Si HMR
 *    recarga este archivo pero el caller mantiene la MISMA instancia
 *    de Pusher viva, una nueva llamada a `instrumentPusher()` montará
 *    un segundo set de listeners. En el dashboard `src/utils/pusher.js`
 *    es un singleton de módulo: si HMR lo reevalúa, también recrea el
 *    Pusher y el problema no aplica. Si en el futuro el lifecycle
 *    cambia, mover el registry a `globalThis`.
 *  - **Ownership único**: el contrato asume UN solo caller dueño del
 *    cleanup. No es ref-counted: si varios módulos llaman
 *    `instrumentPusher(samePusher)`, comparten disposer y el primer
 *    `dispose()` desinstrumenta para todos. En el wire-up actual
 *    (Agente 5) hay un único punto de instrumentación.
 *
 * Usage (wire-up en src/utils/pusher.js, hecho por Agente 5):
 *   import { instrumentPusher } from '../observability/pusherInstrumentation';
 *   const pusher = new Pusher(...);
 *   instrumentPusher(pusher);
 *   export default pusher;
 *
 * @param {object} pusherInstance - Instancia de Pusher (pusher-js)
 * @returns {() => void} dispose function para remover listeners
 */
export function instrumentPusher(pusherInstance) {
  // Validación defensiva: además del nullish check, exigimos que
  // `bind`/`unbind` sean funciones. Esto cubre mocks parciales tipo
  // `{ connection: {} }` que en otra rama crasearían al bind.
  const conn = pusherInstance && pusherInstance.connection;
  if (
    !conn ||
    typeof conn.bind !== 'function' ||
    typeof conn.unbind !== 'function'
  ) {
    return noopDispose;
  }

  // Idempotencia: HMR, re-mount, tests o doble wire-up accidental no
  // deben acumular listeners. Retornamos el disposer REAL almacenado
  // (no un noop) para que el caller, aun sobreescribiendo, conserve
  // capacidad de cleanup.
  const existing = instrumented.get(pusherInstance);
  if (existing) {
    return existing;
  }

  // Map<dedupeKey, lastCapturedAtMs> — vive con el closure del instrumentado,
  // se libera cuando el dispose corre o cuando la instancia es GC.
  const lastCaptureAt = new Map();
  const shouldCapture = (key) => {
    const now = Date.now();
    const prev = lastCaptureAt.get(key);
    if (prev !== undefined && now - prev < CAPTURE_DEDUPE_WINDOW_MS) {
      return false;
    }
    lastCaptureAt.set(key, now);
    return true;
  };

  const onStateChange = (states) => {
    const previous = states && states.previous;
    const current = states && states.current;
    const isDegraded = current === 'failed' || current === 'unavailable';

    Sentry.addBreadcrumb({
      category: 'pusher.connection',
      message: `state: ${previous} → ${current}`,
      level: isDegraded ? 'warning' : 'info',
      data: { previous, current },
    });

    // Al recuperar conexión limpiamos los dedupe keys de estado para
    // que un nuevo ciclo de outage (unavailable -> connected -> unavailable
    // en <30s) se reporte como incidente nuevo, no quede suprimido por
    // la captura anterior.
    if (current === 'connected') {
      lastCaptureAt.delete('state:failed');
      lastCaptureAt.delete('state:unavailable');
    }

    // Estados degradados (failed | unavailable) generan issue agrupable.
    // Incluimos `unavailable` porque en la práctica las quejas de
    // "no me llegan los mensajes" suelen quedarse en retry loops de
    // unavailable y nunca tocan failed. Usamos captureMessage (no
    // captureException) — no hay Error nativo. Dedupe por estado.
    if (isDegraded && shouldCapture(`state:${current}`)) {
      // Mensaje constante por estado + fingerprint explícito para que
      // Sentry agrupe en exactamente dos issues globales (failed y
      // unavailable), no uno por sesión o por tag dinámico.
      const message =
        current === 'failed'
          ? 'Pusher connection failed'
          : 'Pusher connection unavailable';
      Sentry.captureMessage(message, {
        level: 'warning',
        tags: { component: 'pusher', state: current },
        fingerprint: [`pusher-connection-${current}`],
        extra: { previous, current },
      });
    }
  };

  const onError = (err) => {
    // Pusher emite errores con shape { type, error: { message, code } } o similar.
    // No siempre es un Error nativo — normalizar defensivamente.
    const msg =
      (err && err.error && err.error.message) ||
      (err && err.message) ||
      'Pusher error';
    const type = (err && err.type) || 'unknown';
    const code = (err && err.error && err.error.code) || 'unknown';

    Sentry.addBreadcrumb({
      category: 'pusher.error',
      message: msg,
      level: 'error',
      data: { type, code },
    });

    // Dedupe in-memory por type+code — alineado con el fingerprint que
    // mandamos a Sentry. Si incluyéramos msg en la clave, un provider
    // que varía el texto por intento generaría muchas capturas dentro
    // de la ventana para lo que terminará siendo el mismo issue.
    const dedupeKey = `error:${String(type)}:${String(code)}`;
    if (shouldCapture(dedupeKey)) {
      Sentry.captureMessage('Pusher error', {
        level: 'error',
        tags: { component: 'pusher' },
        fingerprint: ['pusher-error', String(type), String(code)],
        extra: { message: msg, pusherError: { type, code } },
      });
    }
  };

  // Bind con rollback: si el segundo bind tira tras el primero exitoso,
  // unbindeamos el primero para no dejar un listener huérfano y
  // propagamos un noop disposer (la instrumentación no quedó activa).
  try {
    conn.bind('state_change', onStateChange);
  } catch (e) {
    Sentry.addBreadcrumb({
      category: 'pusher.instrumentation',
      message: "instrumentPusher() bind threw for 'state_change'",
      level: 'warning',
      data: { error: (e && e.message) || String(e) },
    });
    return noopDispose;
  }
  try {
    conn.bind('error', onError);
  } catch (e) {
    Sentry.addBreadcrumb({
      category: 'pusher.instrumentation',
      message: "instrumentPusher() bind threw for 'error'",
      level: 'warning',
      data: { error: (e && e.message) || String(e) },
    });
    try {
      conn.unbind('state_change', onStateChange);
    } catch (_e2) {
      // Si el rollback también tira, ya no hay nada útil que reportar;
      // el caller verá el breadcrumb anterior.
    }
    return noopDispose;
  }

  const disposePusherInstrumentation = () => {
    // Cada unbind tiene su propio try/catch: si el primero tira, el
    // segundo igual debe intentarse, de lo contrario quedaría un
    // listener leak. Si CUALQUIERA de los dos falla, conservamos el
    // estado (dedupe + idempotency guard) para no permitir que un
    // listener huérfano spammee captureMessage al perder la ventana.
    let unbindFailed = false;
    const safeUnbind = (event, handler) => {
      try {
        // Usamos la referencia `conn` capturada en el cierre — NO
        // `pusherInstance.connection`. pusher-js mantiene la misma
        // `connection` durante la vida del cliente, pero si en el
        // futuro la API la rota (reconexión, multi-cluster, etc.) el
        // unbind debe apuntar al objeto donde realmente registramos.
        conn.unbind(event, handler);
      } catch (e) {
        unbindFailed = true;
        Sentry.addBreadcrumb({
          category: 'pusher.instrumentation',
          message: `dispose() unbind threw for '${event}'`,
          level: 'warning',
          data: { error: (e && e.message) || String(e) },
        });
      }
    };

    safeUnbind('state_change', onStateChange);
    safeUnbind('error', onError);

    if (unbindFailed) {
      // Listeners pueden seguir colgados. NO limpiamos lastCaptureAt:
      // si los handlers siguen vivos, queremos que el dedupe siga
      // suprimiéndolos. Tampoco removemos de `instrumented`, para que
      // un caller que vuelva a llamar instrumentPusher() reciba este
      // mismo disposer (puede reintentar) y no acumule un segundo set.
      return;
    }

    // Camino feliz: cleanup completo. Liberamos dedupe (los handlers ya
    // no van a emitir) y permitimos re-instrumentar la instancia.
    lastCaptureAt.clear();
    instrumented.delete(pusherInstance);
  };

  instrumented.set(pusherInstance, disposePusherInstrumentation);
  return disposePusherInstrumentation;
}

export default instrumentPusher;
